/**
 * String utility functions for "_w" suffix operations.
 * Adheres to SRP (Single Responsibility Principle) and facilitates testing.
 */

/**
 * Adds "_w" suffix to a string if it doesn't already have it
 * @param value The input string
 * @returns The string with "_w" suffix
 */
export function addWebSuffix(value: string): string {
  return value.endsWith('_w') ? value : `${value}_w`;
}

/**
 * Removes "_w" suffix from a string if it exists
 * @param value The input string
 * @returns The string without "_w" suffix
 */
export function removeWebSuffix(value: string): string {
  return value.endsWith('_w') ? value.slice(0, -2) : value;
}

/**
 * Toggles "_w" suffix - adds if not present, removes if present
 * @param value The input string
 * @returns The string with toggled "_w" suffix
 */
export function toggleWebSuffix(value: string): string {
  if (!value || typeof value !== 'string') {
    return value;
  }
  return value.endsWith('_w') ? removeWebSuffix(value) : addWebSuffix(value);
}

/**
 * Checks if a string has "_w" suffix
 * @param value The input string
 * @returns True if string ends with "_w", false otherwise
 */
export function hasWebSuffix(value: string): boolean {
  return !!(value && typeof value === 'string' && value.endsWith('_w'));
}

/**
 * Ensures a string has "_w" suffix (alias for addWebSuffix)
 * @param value The input string
 * @returns The string with "_w" suffix
 */
export function ensureWebSuffix(value: string): string {
  return addWebSuffix(value);
}

/**
 * Ensures a string does NOT have "_w" suffix (alias for removeWebSuffix)
 * @param value The input string
 * @returns The string without "_w" suffix
 */
export function ensureNoWebSuffix(value: string): string {
  return removeWebSuffix(value);
}
